/**
 * Content loading hooks with React Query caching.
 *
 * Provides React Query hooks for loading grammar content, quizzes, and Redemittel
 * with automatic caching, request deduplication, and stale-while-revalidate behavior.
 * Content is cached for 1 hour since it's static and rarely changes.
 *
 * @module useContentLoader
 */

import { useQuery } from '@tanstack/react-query';
import {
  loadGrammarContent,
  loadRedemittelContent,
  type GrammarQuiz,
  type RedemittelCategory,
} from '@/lib/contentLoader';
import type { ProficiencyLevel } from '@/types/learning';

/**
 * Grammar content interface (matches contentLoader.ts)
 */
interface GrammarContent {
  title: string;
  content: string;
  level: string;
  topic: string;
  quiz?: GrammarQuiz | null;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Cache time for content (1 hour in milliseconds).
 * Static content files rarely change, so we can cache them for a long time.
 */
const CONTENT_CACHE_TIME = 1000 * 60 * 60; // 1 hour

/**
 * Stale time for content (30 minutes in milliseconds).
 * Content is considered fresh for 30 minutes, after which it will be refetched in the background.
 */
const CONTENT_STALE_TIME = 1000 * 60 * 30; // 30 minutes

// ============================================================================
// Grammar Content Hooks
// ============================================================================

/**
 * Hook to fetch grammar lesson content and quiz for a specific topic.
 *
 * @param level - Proficiency level (e.g., 'A1', 'B2')
 * @param topic - Topic identifier/slug
 * @returns React Query result with grammar content including lesson and quiz
 *
 * @example
 * ```typescript
 * const { data: content, isLoading, error } = useGrammarContent('A1', 'personalpronomen');
 * ```
 */
export function useGrammarContent(level: string | undefined, topic: string | undefined) {
  return useQuery<GrammarContent | null>({
    queryKey: ['grammarContent', level, topic],
    queryFn: () => {
      if (!level || !topic) {
        return Promise.resolve(null);
      }
      return loadGrammarContent(level, topic);
    },
    enabled: !!level && !!topic,
    staleTime: CONTENT_STALE_TIME,
    gcTime: CONTENT_CACHE_TIME,
  });
}

// ============================================================================
// Redemittel (Common Sentences) Hooks
// ============================================================================

/**
 * Hook to fetch Redemittel (common sentences) content for a specific level.
 *
 * @param level - Proficiency level (e.g., 'A1', 'B2')
 * @returns React Query result with Redemittel categories
 *
 * @example
 * ```typescript
 * const { data: categories, isLoading } = useRedemittelContent('A1');
 * ```
 */
export function useRedemittelContent(level: ProficiencyLevel | undefined) {
  return useQuery<RedemittelCategory[] | null>({
    queryKey: ['redemittelContent', level],
    queryFn: () => {
      if (!level) {
        return Promise.resolve(null);
      }
      return loadRedemittelContent(level);
    },
    enabled: !!level,
    staleTime: CONTENT_STALE_TIME,
    gcTime: CONTENT_CACHE_TIME,
  });
}

